import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { DaySummary, buildCalendar } from '../data/period_insights';
import { DS, formatSigned } from '../shared/Dashboard';

const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const CELL_SIZE = 28;
const CELL_GAP = 4;

/**
 * Stronger colour for bigger swings; played days never fade below a visible
 * minimum so a +1 day still reads as "played".
 */
const cellColor = (summary: DaySummary | undefined, maxSwing: number) => {
    if (!summary) return DS.inset;
    if (summary.totalChange === 0) return alpha(DS.neutral, 0.45);
    const strength = 0.3 + 0.7 * Math.min(Math.abs(summary.totalChange) / maxSwing, 1);
    return alpha(summary.totalChange > 0 ? DS.gain : DS.loss, strength);
};

const SeasonCalendar: React.FC<{ days: DaySummary[] }> = ({ days }) => {
    const months = useMemo(() => buildCalendar(days), [days]);
    const maxSwing = useMemo(() => Math.max(...days.map(d => Math.abs(d.totalChange)), 1), [days]);
    const spansYears = months.length > 0 && !months[0].month.isSame(months[months.length - 1].month, 'year');

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {months.map(({ month, weeks }) => (
                    <Box key={month.format('YYYY-MM')} sx={{ width: 7 * CELL_SIZE + 6 * CELL_GAP }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, mb: 1 }}>
                            {month.format(spansYears ? 'MMMM YYYY' : 'MMMM')}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(7, ${CELL_SIZE}px)`, gap: `${CELL_GAP}px` }}>
                            {WEEKDAY_INITIALS.map((d, i) => (
                                <Typography key={i} sx={{ fontSize: '11px', color: DS.textFaint, textAlign: 'center', lineHeight: 1.6 }}>{d}</Typography>
                            ))}
                            {weeks.flat().map((cell, i) => {
                                if (!cell) return <Box key={`pad${i}`} />;
                                const s = cell.summary;
                                const title = s
                                    ? `${cell.date.format('ddd, MMM D')} · ${s.count} round${s.count !== 1 ? 's' : ''} · ${formatSigned(s.totalChange)} rating`
                                    : `${cell.date.format('ddd, MMM D')} · no rounds`;
                                return (
                                    <Tooltip key={cell.date.format('YYYY-MM-DD')} title={title} arrow disableInteractive>
                                        <Box sx={{
                                            height: CELL_SIZE,
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            fontWeight: s ? 700 : 400,
                                            color: s ? DS.text : DS.textFaint,
                                            opacity: cell.inRange ? 1 : 0.4,
                                            background: s ? cellColor(s, maxSwing) : 'transparent',
                                            border: s ? 'none' : `1px solid ${DS.border}`,
                                            transition: 'transform 0.1s',
                                            '&:hover': { transform: 'scale(1.15)' },
                                        }}>
                                            {cell.date.date()}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>Lost rating</Typography>
                {[1, 0.65, 0.3].map(a => <Box key={`l${a}`} sx={{ width: 12, height: 12, borderRadius: '3px', background: alpha(DS.loss, a) }} />)}
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', background: alpha(DS.neutral, 0.45) }} />
                {[0.3, 0.65, 1].map(a => <Box key={`g${a}`} sx={{ width: 12, height: 12, borderRadius: '3px', background: alpha(DS.gain, a) }} />)}
                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>Gained rating</Typography>
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', border: `1px solid ${DS.border}`, ml: 2 }} />
                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>No rounds</Typography>
            </Box>
        </Box>
    );
};

export default SeasonCalendar;
